import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import mapboxgl from 'mapbox-gl';
import { BaseMapController } from '../core/BaseMapController';
import { IMarkerConfig, ICoordinate } from '../types';
import MapMarker from '../../../components/UI/MapMarker';

interface HTMLElementWithReactRoot extends HTMLElement {
  __reactRoot?: Root;
}

const createReactMarker = (component: React.ReactElement): HTMLElementWithReactRoot => {
  const container = document.createElement('div') as HTMLElementWithReactRoot;
  const root = createRoot(container);
  root.render(component);
  container.__reactRoot = root;
  return container;
};

const updateReactMarker = (container: HTMLElementWithReactRoot, component: React.ReactElement) => {
  const root = container.__reactRoot;
  if (root) root.render(component);
};

const destroyReactMarker = (container: HTMLElementWithReactRoot) => {
  const root = container.__reactRoot;
  if (root) {
    root.unmount();
    delete container.__reactRoot;
  }
};

/**
 * MarkerManager - handles all marker-related functionality
 * - Adding/removing/updating markers
 * - React component rendering
 * - Click handling and interactions
 * - State management
 * - Event-driven architecture for state changes
 */
export class MarkerManager {
  private mapController: BaseMapController;
  private markers: IMarkerConfig[] = [];
  private markerInstances: Map<string, mapboxgl.Marker> = new Map();
  private markerClickHandlers: Map<
    string,
    (markerId: string, screenPos: { x: number; y: number }) => void
  > = new Map();
  private markerDragHandlers: Map<
    string,
    {
      onDragStart?: (id: string, coordinate: ICoordinate) => void;
      onDrag?: (id: string, coordinate: ICoordinate) => void;
      onDragEnd?: (id: string, oldCoordinate: ICoordinate, newCoordinate: ICoordinate) => void;
    }
  > = new Map();

  private eventTarget: EventTarget;

  constructor(mapController: BaseMapController) {
    this.mapController = mapController;
    this.eventTarget = new EventTarget();
  }

  addMarker(marker: IMarkerConfig): void {
    try {
      if (this.markers.find(m => m.id === marker.id)) {
        return;
      }

      this.markers.push(marker);

      const markerComponent = this.createMarkerComponent(marker);

      const container = createReactMarker(markerComponent);

      const mapboxMarker = new mapboxgl.Marker({
        element: container,
        draggable: marker.draggable || false,
      })
        .setLngLat([marker.coordinate.lng, marker.coordinate.lat])
        .addTo(this.mapController.getMap());

      if (marker.draggable) {
        this.setupMarkerDragHandlers(marker, mapboxMarker);
      }

      this.markerInstances.set(marker.id, mapboxMarker);

      this.dispatchMarkerEvent('added', {
        marker,
        totalMarkers: this.markers.length,
        selectedCount: this.getSelectedMarkers().length,
      });
    } catch {
      // Error adding marker
    }
  }

  removeMarker(markerId: string): void {
    try {
      this.markers = this.markers.filter(m => m.id !== markerId);

      const markerInstance = this.markerInstances.get(markerId);
      if (markerInstance) {
        const container = markerInstance.getElement() as HTMLElementWithReactRoot;
        destroyReactMarker(container);
        markerInstance.remove();
        this.markerInstances.delete(markerId);
      }

      this.markerClickHandlers.delete(markerId);

      this.dispatchMarkerEvent('removed', {
        markerId,
        totalMarkers: this.markers.length,
        selectedCount: this.getSelectedMarkers().length,
      });
    } catch {
      // Error removing marker
    }
  }

  updateMarker(marker: IMarkerConfig): void {
    try {
      const existingMarkerIndex = this.markers.findIndex(m => m.id === marker.id);

      if (existingMarkerIndex === -1) {
        console.warn(`Marker with id "${marker.id}" not found for update`);
        return;
      }

      this.markers[existingMarkerIndex] = marker;

      const mapboxMarker = this.markerInstances.get(marker.id);
      if (mapboxMarker) {
        mapboxMarker.setLngLat([marker.coordinate.lng, marker.coordinate.lat]);
      }

      this.updateMarkerElement(marker);

      this.dispatchMarkerEvent('updated', {
        marker,
        totalMarkers: this.markers.length,
        selectedCount: this.getSelectedMarkers().length,
      });
    } catch (error) {
      console.error('Error updating marker:', error);
    }
  }

  getMarkers(): IMarkerConfig[] {
    return [...this.markers];
  }

  hasMarker(id: string): boolean {
    return this.markers.some(marker => marker.id === id);
  }

  getMarker(markerId: string): IMarkerConfig | undefined {
    return this.markers.find(m => m.id === markerId);
  }

  clearMarkers(): void {
    try {
      this.markers.forEach(marker => this.removeMarker(marker.id));
      this.markers = [];
      this.markerInstances.clear();
      this.markerClickHandlers.clear();
      this.markerDragHandlers.clear();

      this.dispatchMarkerEvent('selectionChanged', {
        selectedMarkers: [],
        selectedCount: 0,
        totalMarkers: 0,
      });
    } catch {
      // Error clearing markers
    }
  }

  setMarkerClickHandler(
    markerId: string,
    handler: (markerId: string, screenPos: { x: number; y: number }) => void
  ): void {
    this.markerClickHandlers.set(markerId, handler);
  }

  removeMarkerClickHandler(markerId: string): void {
    this.markerClickHandlers.delete(markerId);
  }

  private handleMarkerClick(marker: IMarkerConfig): void {
    if (marker.onClick) {
      marker.onClick(marker.id);
    }

    const handler = this.markerClickHandlers.get(marker.id);
    if (handler) {
      const markerInstance = this.markerInstances.get(marker.id);
      if (markerInstance) {
        const container = markerInstance.getElement() as HTMLElementWithReactRoot;
        const rect = container.getBoundingClientRect();
        const screenPos = {
          x: rect.left + rect.width / 2,
          y: rect.top,
        };
        handler(marker.id, screenPos);
      }
    }

    if (marker.selected) {
      this.deselectAllMarkers();
    } else {
      this.selectMarker(marker.id);
    }
  }

  private createMarkerComponent(marker: IMarkerConfig) {
    return React.createElement(MapMarker, {
      id: marker.id,
      color: marker.color,
      size: marker.size,
      selected: marker.selected,
      draggable: marker.draggable,
      title: marker.title,
      onClick: () => this.handleMarkerClick(marker),
      markerManager: this,
    });
  }

  updateMarkerElement(marker: IMarkerConfig): void {
    const markerInstance = this.markerInstances.get(marker.id);
    if (markerInstance) {
      const container = markerInstance.getElement() as HTMLElementWithReactRoot;
      const markerComponent = this.createMarkerComponent(marker);
      updateReactMarker(container, markerComponent);
    }
  }

  getStats(): { total: number; selected: number } {
    const selected = this.markers.filter(m => m.selected).length;
    return {
      total: this.markers.length,
      selected,
    };
  }

  selectAllMarkers(selected: boolean = true): void {
    this.markers.forEach(marker => {
      marker.selected = selected;
      this.updateMarkerElement(marker);
    });

    this.dispatchMarkerEvent('selectionChanged', {
      selectedCount: this.getSelectedMarkers().length,
      totalMarkers: this.markers.length,
    });
  }

  getSelectedMarkers(): IMarkerConfig[] {
    return this.markers.filter(m => m.selected);
  }

  getMarkersInBounds(bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }): IMarkerConfig[] {
    return this.markers.filter(marker => {
      const { lat, lng } = marker.coordinate;
      return lat <= bounds.north && lat >= bounds.south && lng <= bounds.east && lng >= bounds.west;
    });
  }

  private setupMarkerDragHandlers(marker: IMarkerConfig, mapboxMarker: mapboxgl.Marker): void {
    let startCoordinate: ICoordinate;

    this.markerDragHandlers.set(marker.id, {
      onDragStart: marker.onDragStart,
      onDrag: marker.onDrag,
      onDragEnd: marker.onDragEnd,
    });

    mapboxMarker.on('dragstart', () => {
      const currentMarker = this.getMarker(marker.id);
      startCoordinate = currentMarker ? { ...currentMarker.coordinate } : { ...marker.coordinate };

      if (marker.onDragStart) {
        marker.onDragStart(marker.id, startCoordinate);
      }
    });

    mapboxMarker.on('drag', () => {
      const lngLat = mapboxMarker.getLngLat();
      const currentCoordinate: ICoordinate = {
        lat: lngLat.lat,
        lng: lngLat.lng,
      };

      if (marker.onDrag) {
        marker.onDrag(marker.id, currentCoordinate);
      }
    });

    mapboxMarker.on('dragend', () => {
      const lngLat = mapboxMarker.getLngLat();
      const endCoordinate: ICoordinate = {
        lat: lngLat.lat,
        lng: lngLat.lng,
      };

      if (marker.onDragEnd) {
        marker.onDragEnd(marker.id, startCoordinate, endCoordinate);
      }
    });
  }

  setMarkerDragHandlers(
    markerId: string,
    handlers: {
      onDragStart?: (id: string, coordinate: ICoordinate) => void;
      onDrag?: (id: string, coordinate: ICoordinate) => void;
      onDragEnd?: (id: string, oldCoordinate: ICoordinate, newCoordinate: ICoordinate) => void;
    }
  ): void {
    this.markerDragHandlers.set(markerId, handlers);

    const marker = this.markers.find(m => m.id === markerId);
    if (marker) {
      marker.onDragStart = handlers.onDragStart;
      marker.onDrag = handlers.onDrag;
      marker.onDragEnd = handlers.onDragEnd;
    }
  }

  removeMarkerDragHandlers(markerId: string): void {
    this.markerDragHandlers.delete(markerId);
  }

  addEventListener(type: string, listener: EventListener): void {
    this.eventTarget.addEventListener(type, listener);
  }

  removeEventListener(type: string, listener: EventListener): void {
    this.eventTarget.removeEventListener(type, listener);
  }

  selectMarker(markerId: string): void {
    const selectedMarkers = this.getSelectedMarkers();
    for (const selectedMarker of selectedMarkers) {
      if (selectedMarker.id !== markerId) {
        selectedMarker.selected = false;
        this.updateMarkerElement(selectedMarker);
      }
    }

    const marker = this.getMarker(markerId);
    if (marker) {
      marker.selected = true;
      this.updateMarkerElement(marker);
    }

    this.dispatchMarkerEvent('select', { markerId });
    this.dispatchMarkerEvent('selectionChanged', {
      selectedCount: this.getSelectedMarkers().length,
      totalMarkers: this.markers.length,
    });
  }

  deselectAllMarkers(): void {
    const selectedMarkers = this.getSelectedMarkers();
    for (const selectedMarker of selectedMarkers) {
      selectedMarker.selected = false;
      this.updateMarkerElement(selectedMarker);
    }

    this.dispatchMarkerEvent('select', { markerId: null });
    this.dispatchMarkerEvent('selectionChanged', {
      selectedCount: 0,
      totalMarkers: this.markers.length,
    });
  }

  hoverMarker(markerId: string | null): void {
    this.dispatchMarkerEvent('hover', { markerId });
  }

  private dispatchMarkerEvent(
    type: 'added' | 'removed' | 'updated' | 'selectionChanged' | 'select' | 'hover',
    data: {
      marker?: IMarkerConfig;
      markerId?: string | null;
      selectedMarkers?: IMarkerConfig[];
      totalMarkers?: number;
      selectedCount?: number;
    }
  ): void {
    const event = new CustomEvent(`marker:${type}`, {
      detail: {
        type,
        ...data,
        timestamp: Date.now(),
      },
    });

    this.eventTarget.dispatchEvent(event);
  }
}
